import { useEffect, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Alert, Avatar, Button, Card, Flex, Input, List, Modal } from 'antd';
import { LuSearch } from 'react-icons/lu';

import fallbackImage from '@assets/fallbackImage.jpg';
import BaseText from '@components/typography/BaseText';
import useDebounce from '@hooks/useDebounce';
import { useThumbnailUrl } from '@hooks/useThumbnailUrl';
import { getProjects } from '@utils/api';
import { PROJECT_TYPE_LABELS } from '@utils/constants';
import { projectModeFromImageMode } from '@utils/projectMode';

// Past this many projects the list gets a search box; below it, scanning is quicker.
const SEARCH_THRESHOLD = 3;
const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;
const LIST_MAX_HEIGHT = 320;
// Pages one fill may scan before it waits for "Load more", so a type with few projects
// does not walk the whole list unasked.
const MAX_FILL_PAGES = 4;
const INITIAL_FILL = { rows: PAGE_SIZE, pages: MAX_FILL_PAGES };

const projectModeOf = project => projectModeFromImageMode(project?.image_mode);

const modeLabel = mode => PROJECT_TYPE_LABELS[mode] ?? '';

// A project whose orthophoto is still processing has no usable imagery yet, so it is
// left out of the list rather than offered with a disabled action.
const isProjectReady = project => {
	const center = project?.center_coordinate;
	if (project?.orthophoto_progress?.error) return false;
	return Array.isArray(center) && !(center[0] === -1 && center[1] === -1);
};

/** Signed orthophoto thumbnail for one row, same key the project cards use. */
const ProjectRowThumbnail = ({ uuid }) => {
	const url = useThumbnailUrl(uuid ? `${uuid}/odm_orthophoto/thumbnail.png` : null);

	return <Avatar shape="square" size={48} src={url ?? fallbackImage} alt="" />;
};

/**
 * "Choose a project" dialog: a searchable, paged list of the user's projects
 * with one action per row. Search runs on the server, so it covers every
 * project rather than only the pages loaded so far. The project-type filter
 * (`mode`) and the readiness check are applied to each loaded page on the client,
 * as the Collections page does, since the projects endpoint has no such parameter;
 * only projects with usable imagery are listed. Paging still
 * follows the server's totals, so a page can add fewer rows than its size. It only reports the chosen project — what happens next
 * (open it to annotate, run a detection, …) is the caller's decision, which
 * is what lets the object details panel and the collections page share it.
 *
 * @param {boolean} props.open
 * @param {string} [props.title]
 * @param {import('react').ReactNode} [props.subtitle] - why the user is picking a project
 * @param {string} [props.mode] - a `PROJECT_MODE` value; only projects of that type are listed
 * @param {string} [props.actionLabel] - label of the per-row button, e.g. "Annotate"
 * @param {(project: object) => void} props.onSelect - receives the raw project from the API
 * @param {() => void} props.onCancel
 */
const ProjectPickerModal = ({
	open,
	title = 'Choose a project',
	subtitle,
	mode,
	actionLabel = 'Select',
	onSelect,
	onCancel,
}) => {
	// `query` follows the input; `search` is what the server was last asked for.
	const [query, setQuery] = useState('');
	const [search, setSearch] = useState('');
	// When to stop filling. The filters run on the client, so a page can add few rows or
	// none; pages keep loading until this many rows are listed or this many pages loaded.
	const [fill, setFill] = useState(INITIAL_FILL);
	const updateSearch = useDebounce(value => {
		setSearch(value.trim());
		setFill(INITIAL_FILL);
	}, SEARCH_DEBOUNCE_MS);

	const {
		data,
		isLoading,
		isError,
		isFetching,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
		isFetchNextPageError,
	} = useInfiniteQuery({
		queryKey: ['project_picker', search],
		queryFn: ({ pageParam }) => getProjects({ limit: PAGE_SIZE, offset: pageParam, search }),
		initialPageParam: 0,
		getNextPageParam: (lastPage, allPages) => {
			// An empty page ends the list even if `total` says otherwise, or the
			// same offset would be asked for again.
			if (!lastPage.projects?.length) return undefined;
			const loaded = allPages.reduce((sum, page) => sum + (page.projects?.length ?? 0), 0);
			return loaded < (lastPage.total ?? 0) ? loaded : undefined;
		},
		enabled: open,
	});

	const loadedProjects = data?.pages?.flatMap(page => page.projects ?? []) ?? [];
	const projects = loadedProjects.filter(
		project => isProjectReady(project) && (!mode || projectModeOf(project) === mode)
	);
	const total = data?.pages?.at(-1)?.total ?? 0;
	const pageCount = data?.pages?.length ?? 0;
	const isFilling =
		open && Boolean(hasNextPage) && projects.length < fill.rows && pageCount < fill.pages;
	// A later page failing keeps the pages already loaded: only a failed first page
	// leaves nothing to list.
	const isListError = isError && !isFetchNextPageError;

	// Keyed on the page count too: a fast page can land without a render ever seeing
	// `isFetching` turn true, and the next page would then never be asked for. Never
	// after an error, or a failing page would be retried in a loop; "Retry" asks again.
	useEffect(() => {
		if (isFilling && !isFetching && !isError) fetchNextPage();
	}, [isFilling, isFetching, isError, pageCount, fetchNextPage]);

	const typeWord = mode ? `${modeLabel(mode).toLowerCase()} ` : '';
	// Keep the box once a search is in play, or a narrowing result would hide it.
	const showSearch = total > SEARCH_THRESHOLD || Boolean(search);

	const handleQueryChange = event => {
		setQuery(event.target.value);
		updateSearch(event.target.value);
	};

	const handleClose = () => {
		setQuery('');
		setSearch('');
		setFill(INITIAL_FILL);
		onCancel?.();
	};

	const loadMoreFailed = isFetchNextPageError ? (
		<Alert
			type="warning"
			showIcon
			title="Couldn’t load more projects."
			action={
				<Button size="small" onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
					Retry
				</Button>
			}
			style={{ marginBlockStart: 12 }}
		/>
	) : null;

	const loadMore =
		loadMoreFailed ??
		(hasNextPage ? (
			<Flex justify="center" style={{ marginBlockStart: 12 }}>
				<Button
					onClick={() =>
						setFill({
							rows: projects.length + PAGE_SIZE,
							pages: pageCount + MAX_FILL_PAGES,
						})
					}
					loading={isFetchingNextPage}>
					Load more
				</Button>
			</Flex>
		) : null);

	return (
		<Modal
			open={open}
			onCancel={handleClose}
			footer={null}
			mask={false}
			destroyOnHidden
			title={title}>
			<Flex vertical gap={10}>
				{subtitle && (
					<BaseText type="secondary" size="xs">
						{subtitle}
					</BaseText>
				)}
				{showSearch && (
					<Input
						allowClear
						prefix={<LuSearch aria-hidden="true" />}
						placeholder="Search projects"
						aria-label="Search projects"
						value={query}
						onChange={handleQueryChange}
					/>
				)}

				{isListError && (
					<Alert type="error" showIcon message="Could not load your projects." />
				)}

				{!isListError && (
					<List
						// Still scanning pages for a first match: not "no projects" yet.
						loading={
							isLoading ||
							(isFilling && !isFetchNextPageError && projects.length === 0)
						}
						dataSource={projects}
						loadMore={loadMore}
						locale={{
							// More pages left after a fill found nothing: none so far, not none at all.
							emptyText: isFetchNextPageError
								? 'None found yet.'
								: hasNextPage
									? 'None found yet. Load more to keep looking.'
									: search
										? `No ${typeWord}projects match “${search}”.`
										: `No ${typeWord}projects yet.`,
						}}
						split={false}
						style={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto' }}
						renderItem={project => (
							<List.Item>
								<Card size="small" style={{ width: '100%' }}>
									<Flex align="center" gap={12}>
										<ProjectRowThumbnail uuid={project.uuid} />
										<Flex vertical style={{ flex: 1, minWidth: 0 }}>
											<BaseText strong ellipsis size="sm">
												{project.title || 'Untitled project'}
											</BaseText>
											<BaseText type="secondary" size="xs">
												{modeLabel(projectModeOf(project))}
											</BaseText>
										</Flex>
										<Button type="primary" onClick={() => onSelect?.(project)}>
											{actionLabel}
										</Button>
									</Flex>
								</Card>
							</List.Item>
						)}
					/>
				)}
			</Flex>
		</Modal>
	);
};

export default ProjectPickerModal;
